import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { locationAccessMutation, siteAccessMutation } from '@queries';
import { ButtonSpinnerDarkIcon, MoreHorizontalIcon, TrashIcon } from '@svgs';

import './User.scss';

const User = ({
  disablingInProgress,
  locationAccessData,
  siteAccessData,
  siteData,
  siteID,
  updateDisablingInProgress,
  user,
}) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  // save business, location, site access
  const mutations = {
    location: useMutation(
      locationAccessMutation(siteData.locations[0].id, {
        onSuccess: () => {
          // invalidate the query so it is fetched again
          queryClient.invalidateQueries({
            queryKey: ['locations_access', 'list', siteData.locations[0].id],
          });
        },
      }),
    ),
    site: useMutation(
      siteAccessMutation(siteID, {
        onSuccess: () => {
          // invalidate the query so it is fetched again
          queryClient.invalidateQueries({
            queryKey: ['sites_access', 'list', siteID],
          });
          // update the shared state
          updateDisablingInProgress(false);
        },
      }),
    ),
  };

  const onRemoveAccess = async (e) => {
    // note: we don't remove business or template site access currently

    // update the shared state
    updateDisablingInProgress(true);

    // filter list to remove current user from location access
    const newLocationList = [...locationAccessData].filter(
      (u) => u.id != user.id,
    );
    mutations.location.mutate(newLocationList);

    // filter list to remove current user from site access
    const newSiteList = [...siteAccessData].filter((u) => u.id != user.id);
    mutations.site.mutate(newSiteList);
  };

  // combined isPending
  const isPending = mutations.location.isPending || mutations.site.isPending;
  // combined isSuccess
  const isSuccess = mutations.location.isSuccess || mutations.site.isSuccess;

  // menu activation
  const dropdownMenu = useRef(null);
  const [showDropdownMenu, setShowDropdownMenu] = useState(false);
  const closeOpenMenus = (e) => {
    if (showDropdownMenu && !dropdownMenu.current?.contains(e.target)) {
      setShowDropdownMenu(false);
    }
  };
  document.addEventListener('mousedown', closeOpenMenus);

  return (
    <div className="sb-row-item">
      <div>
        <span className="sb-accessuser-row-icon">
          {user.first_name[0].toUpperCase()}
        </span>
      </div>
      <div className="sb-row--accessuser-name">
        <p>
          <b>
            {user.first_name} {user.last_name}
          </b>
        </p>
      </div>
      <div className="sb-row--accessuser-email">
        <p>{user.email}</p>
      </div>
      <div className="sb-options">
        {(isPending || isSuccess) && <ButtonSpinnerDarkIcon />}
        {!isPending && !isSuccess && (
          <>
            <button
              className="sb-page-options-menu-trigger sb-options-trigger"
              disabled={disablingInProgress}
              onClick={() => setShowDropdownMenu(!showDropdownMenu)}
            >
              <MoreHorizontalIcon />
            </button>
            <div
              ref={dropdownMenu}
              className={
                'sb-options-menu ' + (showDropdownMenu ? 'sb-show' : 'sb-hide')
              }
            >
              <div className="sb-options-menu-content">
                <button
                  type="button"
                  className="sb-options-item"
                  onClick={onRemoveAccess}
                >
                  <TrashIcon />
                  {t('Remove access')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default User;
